import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Users, Clock, CheckCircle, AlertTriangle, Download, 
  RefreshCw, User, Calendar, Filter, BarChart3, Search
} from 'lucide-react';

export default function VendorFieldAgentReport({ checks = [], onRefresh }) {
  const [fieldAgentFilter, setFieldAgentFilter] = useState('all');
  const [tatFilter, setTatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('summary');

  // Get unique field agents
  const fieldAgents = useMemo(() => {
    const agents = new Map();
    checks.forEach(c => {
      const name = c.field_agent_name || c.field_agent?.name;
      if (name) {
        if (!agents.has(name)) {
          agents.set(name, { 
            name, 
            phone: c.field_agent_phone || c.field_agent?.phone || '',
            total: 0, 
            assigned: 0,
            completed: 0, 
            withinTat: 0, 
            overTat: 0 
          });
        }
        const agent = agents.get(name);
        agent.total++;
        
        const isCompleted = c.status === 'completed' || c.field_verification_status === 'completed';
        if (isCompleted) {
          agent.completed++;
        } else {
          agent.assigned++;
        }
        
        // TAT calculation
        const now = new Date();
        const assignedDate = new Date(c.assigned_at || c.vendor_assigned_at || c.created_at);
        const tatDays = c.agent_tat || c.tat_days || 7;
        const dueDate = new Date(assignedDate);
        dueDate.setDate(dueDate.getDate() + tatDays);
        
        if (!isCompleted) {
          if (now > dueDate) {
            agent.overTat++;
          } else {
            agent.withinTat++;
          }
        }
      }
    });
    return Array.from(agents.values());
  }, [checks]);

  // Calculate overall stats
  const stats = useMemo(() => {
    let total = 0, assigned = 0, completed = 0, withinTat = 0, overTat = 0;
    const now = new Date();
    
    checks.forEach(c => {
      if (c.field_agent_name || c.field_agent?.name) {
        total++;
        const isCompleted = c.status === 'completed' || c.field_verification_status === 'completed';
        
        if (isCompleted) {
          completed++;
        } else {
          assigned++;
          
          const assignedDate = new Date(c.assigned_at || c.vendor_assigned_at || c.created_at);
          const tatDays = c.agent_tat || c.tat_days || 7;
          const dueDate = new Date(assignedDate);
          dueDate.setDate(dueDate.getDate() + tatDays);
          
          if (now > dueDate) {
            overTat++;
          } else {
            withinTat++;
          }
        }
      }
    });
    
    return { total, assigned, completed, withinTat, overTat };
  }, [checks]);

  // Filter checks
  const filteredChecks = useMemo(() => {
    let filtered = checks.filter(c => c.field_agent_name || c.field_agent?.name);
    
    // Field Agent filter
    if (fieldAgentFilter !== 'all') {
      filtered = filtered.filter(c => 
        c.field_agent_name === fieldAgentFilter || c.field_agent?.name === fieldAgentFilter
      );
    }
    
    // TAT filter
    if (tatFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(c => {
        const isCompleted = c.status === 'completed' || c.field_verification_status === 'completed';
        if (isCompleted && tatFilter !== 'all') return tatFilter === 'completed';
        
        const assignedDate = new Date(c.assigned_at || c.vendor_assigned_at || c.created_at);
        const tatDays = c.agent_tat || c.tat_days || 7;
        const dueDate = new Date(assignedDate);
        dueDate.setDate(dueDate.getDate() + tatDays);
        const isOverTat = now > dueDate;
        
        if (tatFilter === 'within_tat') return !isOverTat && !isCompleted;
        if (tatFilter === 'over_tat') return isOverTat && !isCompleted;
        if (tatFilter === 'completed') return isCompleted;
        return true;
      });
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => {
        if (statusFilter === 'assigned') return c.status !== 'completed' && c.field_verification_status !== 'completed';
        if (statusFilter === 'completed') return c.status === 'completed' || c.field_verification_status === 'completed';
        return c.status === statusFilter;
      });
    }
    
    // Date filters (assigned date)
    if (dateFrom) {
      filtered = filtered.filter(c => 
        new Date(c.assigned_at || c.vendor_assigned_at || c.created_at) >= new Date(dateFrom)
      );
    }
    if (dateTo) {
      filtered = filtered.filter(c => 
        new Date(c.assigned_at || c.vendor_assigned_at || c.created_at) <= new Date(dateTo + 'T23:59:59')
      );
    }
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.case_id?.toLowerCase().includes(query) ||
        c.check_id?.toLowerCase().includes(query) ||
        c.employee_name?.toLowerCase().includes(query) ||
        (c.field_agent_name || '').toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [checks, fieldAgentFilter, tatFilter, statusFilter, dateFrom, dateTo, searchQuery]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const clearFilters = () => {
    setFieldAgentFilter('all');
    setTatFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Field Agent Report</h2>
          <p className="text-sm text-slate-500">Track field agent assignments and performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-slate-500" onClick={() => { setTatFilter('all'); setStatusFilter('all'); }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Assigned</p>
                <p className="text-2xl font-bold text-slate-700">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-amber-500" onClick={() => { setTatFilter('all'); setStatusFilter('assigned'); }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Assigned</p>
                <p className="text-2xl font-bold text-amber-700">{stats.assigned}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-emerald-500" onClick={() => { setTatFilter('completed'); setStatusFilter('all'); }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500" onClick={() => { setTatFilter('within_tat'); setStatusFilter('all'); }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Within TAT</p>
                <p className="text-2xl font-bold text-green-700">{stats.withinTat}</p>
              </div>
              <Clock className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500" onClick={() => { setTatFilter('over_tat'); setStatusFilter('all'); }}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Over TAT</p>
                <p className="text-2xl font-bold text-red-700">{stats.overTat}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button 
          variant={activeView === 'summary' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setActiveView('summary')}
        >
          <BarChart3 className="w-4 h-4 mr-2" />Agent Summary
        </Button>
        <Button 
          variant={activeView === 'details' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setActiveView('details')}
        >
          <Search className="w-4 h-4 mr-2" />Detailed View
        </Button>
      </div>

      {/* Agent Summary View */}
      {activeView === 'summary' && (
        <Card>
          <CardHeader className="py-3 border-b">
            <CardTitle className="text-lg">Field Agent Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-100">
                <TableRow>
                  <TableHead className="font-semibold">Field Agent</TableHead>
                  <TableHead className="font-semibold">Phone</TableHead>
                  <TableHead className="text-center font-semibold">Total</TableHead>
                  <TableHead className="text-center font-semibold">Assigned</TableHead>
                  <TableHead className="text-center font-semibold">Completed</TableHead>
                  <TableHead className="text-center font-semibold">Within TAT</TableHead>
                  <TableHead className="text-center font-semibold">Over TAT</TableHead>
                  <TableHead className="text-center font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fieldAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No field agents assigned yet
                    </TableCell>
                  </TableRow>
                ) : (
                  fieldAgents.map((agent, idx) => (
                    <TableRow key={idx} className="hover:bg-slate-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-purple-500" />
                          {agent.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">{agent.phone || '-'}</TableCell>
                      <TableCell className="text-center font-medium">{agent.total}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-amber-50 text-amber-700">{agent.assigned}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{agent.completed}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-green-50 text-green-700">{agent.withinTat}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-red-50 text-red-700">{agent.overTat}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setFieldAgentFilter(agent.name); setActiveView('details'); }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Detailed View */}
      {activeView === 'details' && (
        <>
          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-sm">Filters</span>
                <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={clearFilters}>
                  Clear All
                </Button>
              </div>
              <div className="grid grid-cols-6 gap-4">
                <div>
                  <Label className="text-xs">Field Agent</Label>
                  <Select value={fieldAgentFilter} onValueChange={setFieldAgentFilter}>
                    <SelectTrigger><SelectValue placeholder="All Agents" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      {fieldAgents.map(a => (
                        <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Assigned Date</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Completed Date</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">TAT Status</Label>
                  <Select value={tatFilter} onValueChange={setTatFilter}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="within_tat">Within TAT</SelectItem>
                      <SelectItem value="over_tat">Over TAT</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Search</Label>
                  <Input 
                    placeholder="Case/Check ID, Employee..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card>
            <CardHeader className="py-3 border-b">
              <CardTitle className="text-lg">
                Field Agent Assignments ({filteredChecks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-slate-100">
                  <TableRow>
                    <TableHead className="font-semibold">Case ID</TableHead>
                    <TableHead className="font-semibold">Check ID</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Field Agent</TableHead>
                    <TableHead className="font-semibold">Assigned Date</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">TAT</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChecks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredChecks.map((check, idx) => {
                      const isCompleted = check.status === 'completed' || check.field_verification_status === 'completed';
                      const now = new Date();
                      const assignedDate = new Date(check.assigned_at || check.vendor_assigned_at || check.created_at);
                      const tatDays = check.agent_tat || check.tat_days || 7;
                      const dueDate = new Date(assignedDate);
                      dueDate.setDate(dueDate.getDate() + tatDays);
                      const daysElapsed = Math.ceil((now - assignedDate) / (1000 * 60 * 60 * 24));
                      const isOverTat = now > dueDate && !isCompleted;
                      
                      return (
                        <TableRow key={idx} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm">{check.case_id}</TableCell>
                          <TableCell className="font-mono text-sm">{check.check_id || check.id}</TableCell>
                          <TableCell>{check.employee_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-purple-500" />
                              {check.field_agent_name || check.field_agent?.name}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(check.assigned_at || check.vendor_assigned_at)}</TableCell>
                          <TableCell>
                            <Badge className={isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {isCompleted ? 'Completed' : 'Assigned'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${isOverTat ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                              {daysElapsed}/{tatDays}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
